/**
 * Recording uploader with two modes behind one interface:
 *
 *  - STREAMING: opens a server session (POST /recordings/session) and streams
 *    each MediaRecorder chunk to /recordings/:id/segment during the call, then
 *    seals it with /recordings/:id/finalize. Chunks are discarded as they're
 *    acked, so browser memory stays flat over a 60-minute call (fixes R2), and
 *    a recorder tab that crashes mid-call leaves the bytes on the server (the
 *    server auto-finalizes after the heartbeat lapses — fixes R1).
 *
 *  - LEGACY: if the server has no segmented endpoints (old build) or the
 *    session can't be opened, every chunk is buffered in memory and POSTed as a
 *    single multipart request at stop time — exactly the previous behaviour.
 *
 * The mode is decided once, when the session-open probe resolves; until then
 * chunks are buffered so neither path loses data. WebM is a streaming
 * container, so concatenating the in-order chunks server-side reproduces the
 * exact same file `new Blob(chunks)` would have — no remux needed.
 */
import { getAuthToken } from "lib/providers";
import config from "../config";

export type UploadPhase = "uploading" | "processing";

export interface UploadProgress {
  loaded: number;
  total: number;
  phase: UploadPhase;
}

export interface RecordingResult {
  url: string;
  filename: string;
  size: number;
  id?: string;
}

export interface RecordingMetadata {
  conferenceId?: string;
  threadId?: string;
  participants?: Array<{ userId: string; screenName: string }>;
}

// See the matching comments in useConferenceRecording.ts — the idle timeout is
// reset on every progress event so multi-GB legacy uploads on slow links don't
// abort, and the hard cap is a last-resort sanity bound.
const UPLOAD_IDLE_TIMEOUT_MS = 180_000;
const UPLOAD_MAX_DURATION_MS = 3 * 60 * 60 * 1000;
const HEARTBEAT_INTERVAL_MS = 30_000;
const SEGMENT_RETRY_BASE_MS = 500;
// Cap on the exponential backoff between segment retries. While the recording
// is live we retry transient failures *indefinitely* (a network drop must not
// lose the tail — the chunk stays queued until it sends, exactly like the
// legacy buffer-everything path), so the backoff is capped rather than counted.
const SEGMENT_RETRY_CAP_MS = 30_000;
// At finalize time we can't wait forever for a still-down network, so un-acked
// segments are given this long to flush before we seal with what the server
// has (the server-side staging already holds everything that did get through).
const FINALIZE_DRAIN_MS = 180_000;

// BANDWIDTH BACK-PRESSURE (2026-06-15)
//
// Conference reports showed calls dropping at the 2-3 minute mark while
// recording was on. The recorder competes with the producer for the same
// home-upstream bandwidth: 1-2 Mbps of WebRTC video + the recording's
// 1-second segments. When that combination saturates the link, RTP
// packets get queued, ICE goes "disconnected", and the consumer side
// declares the producer dead.
//
// The fix is cooperative: when the network-quality hook reports a poor
// score, the recording uploader pauses *segment transmission* (segments
// keep buffering up to a generous cap so we don't lose recording when
// quality eventually recovers). When the score climbs back the pump
// resumes and drains the backlog.
//
// `useConferenceRecording` (the only caller of this module) hooks the
// quality readings via `setRecordingUploadPaused`.
let uploadPaused = false;
const pauseListeners = new Set<() => void>();
/**
 * External knob — called by `useConferenceRecording` whenever the
 * conference's `useNetworkQuality.quality` rating crosses the
 * "good enough to keep uploading" threshold (currently 3 of 5).
 * Pausing only stops segment *transmission*; the MediaRecorder keeps
 * producing chunks and they queue in memory.
 */
export function setRecordingUploadPaused(paused: boolean): void {
  if (uploadPaused === paused) return;
  uploadPaused = paused;
  // Promoted to console.warn so it lands in diagnosticDump — long
  // PAUSE windows (we've seen 100+ s in prod, 1-on-1 dev SFU) are
  // the canonical signal that network quality is bad and the call
  // is about to die. Without it being in the dump we can't
  // post-mortem why a 3-min call dropped.
  // eslint-disable-next-line no-console
  console.warn(`[recording] upload ${paused ? "PAUSED" : "RESUMED"} via setRecordingUploadPaused — pending pumps will ${paused ? "stall" : "drain"}`);
  if (!paused) {
    // Wake every paused pump.
    for (const wake of Array.from(pauseListeners)) {
      try { wake(); } catch { /* listener already torn down */ }
    }
  }
}

// `bail` lets a caller (the finalize drain) stop waiting on the pause flag once
// its own deadline passes — otherwise a recording that stops while the link is
// "paused" (quality<=2) would stall its drain forever and finalize empty.
function waitWhilePaused(bail?: () => boolean): Promise<void> {
  if (!uploadPaused || bail?.()) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const wake = () => { pauseListeners.delete(wake); clearInterval(poll); resolve(); };
    pauseListeners.add(wake);
    // Safety: re-check in case the listener was missed (e.g. set during a
    // microtask race) or the bail deadline passed while paused. Cheap — the
    // flag is module-local. Poll faster when a deadline-bail is in play.
    const poll = setInterval(() => {
      if (!uploadPaused || bail?.()) {
        clearInterval(poll);
        pauseListeners.delete(wake);
        resolve();
      }
    }, bail ? 1000 : 5000);
  });
}

function sanitizeUserId(raw: string): string {
  return String(raw).replace(/[^a-zA-Z0-9_-]/g, "");
}

function getApiBase(): string {
  const apiUrl = config.apiUrl || "/graphql";
  if (apiUrl.startsWith("/")) return window.location.origin;
  return apiUrl.replace(/\/graphql\/?$/, "").replace(/\/$/, "");
}

/** RFC4122 v4 id (crypto.randomUUID where available, else a getRandomValues fallback). */
export function mintRecordingId(): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c: any = (globalThis as any).crypto;
  try {
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
    if (c && typeof c.getRandomValues === "function") {
      const b = c.getRandomValues(new Uint8Array(16));
      b[6] = (b[6] & 0x0f) | 0x40;
      b[8] = (b[8] & 0x3f) | 0x80;
      const h = Array.from(b as Uint8Array, (x) => x.toString(16).padStart(2, "0")).join("");
      return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
    }
  } catch { /* fall through */ }
  // Last resort — not RFC-strict but unique enough for an id, and still matches
  // the server's [a-f0-9-] filename charset.
  const rnd = () => Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, "0");
  return `${rnd()}-${rnd().slice(0, 4)}-4${rnd().slice(0, 3)}-a${rnd().slice(0, 3)}-${rnd()}${rnd().slice(0, 4)}`;
}

type StreamKind = "video" | "audio";

interface StreamState {
  kind: StreamKind;
  userId?: string; // for audio
  mime: string;
  offsetMs: number;
  // Retained for the LEGACY single-POST path (and as the buffer while probing).
  // Nulled out once STREAMING is confirmed (chunks then live only in `queue`
  // until acked, keeping memory flat).
  legacyChunks: Blob[];
  // STREAMING send queue: chunks waiting to be acked, in order.
  queue: Array<{ seq: number; blob: Blob }>;
  produced: number; // total chunks pushed == next seq
  pumping: Promise<void> | null;
  failed: boolean;
}

type Mode = "probing" | "streaming" | "legacy" | "broken";

export interface RecordingUploader {
  recordingId: string;
  pushVideo(blob: Blob, mime: string): void;
  pushAudio(userId: string, mime: string, offsetMs: number, blob: Blob): void;
  finalize(durationSec: number, onProgress?: (p: UploadProgress) => void): Promise<RecordingResult | null>;
  abort(): void;
  /** Stop streaming on unmount without discarding the server session (it auto-finalizes). */
  detach(): void;
}

/**
 * Tell the server the "Recording available" chat message has been posted, so
 * its auto-announce safety net (sweepUnannouncedRecordings) won't re-post it.
 * Best-effort: if this call fails, the server simply re-posts after its grace
 * period — strictly better than a recording that never appears in the thread.
 */
export async function markRecordingAnnounced(recordingId: string): Promise<void> {
  if (!recordingId) return;
  try {
    const base = getApiBase();
    let token: string | null = null;
    try { token = await getAuthToken(); } catch { token = null; }
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = token;
    await fetch(`${base}/recordings/${encodeURIComponent(recordingId)}/announced`, {
      method: "POST",
      credentials: "include",
      headers,
    });
  } catch {
    /* best-effort */
  }
}

export function createRecordingUploader(meta?: RecordingMetadata, videoMimeInit?: string): RecordingUploader {
  const recordingId = mintRecordingId();
  const base = getApiBase();
  let mode: Mode = "probing";
  let token: string | null = null;
  let videoMime = videoMimeInit || "video/webm";
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  let aborted = false;
  // detached: component unmounted mid-recording — stop sending but leave the
  // server session for its sweep to auto-finalize (don't discard like abort).
  let detached = false;
  // finalizing: stop() was called; un-acked segments now drain against a
  // deadline instead of retrying forever.
  let finalizing = false;
  let drainDeadline = 0;

  const video: StreamState = {
    kind: "video", mime: videoMime, offsetMs: 0,
    legacyChunks: [], queue: [], produced: 0, pumping: null, failed: false,
  };
  const audio = new Map<string, StreamState>();
  const allStreams = (): StreamState[] => [video, ...audio.values()];

  async function refreshToken(): Promise<void> {
    try { token = await getAuthToken(); } catch { token = null; }
  }

  function authHeaders(extra?: Record<string, string>): Record<string, string> {
    const h: Record<string, string> = { ...(extra || {}) };
    if (token) h.Authorization = token;
    return h;
  }

  // Open the session. Resolves the mode: streaming on 2xx, legacy otherwise
  // (old server with no /session route, clash, or network error).
  const ready: Promise<void> = (async () => {
    await refreshToken();
    const params = new URLSearchParams({ recordingId, videoMime });
    if (meta?.conferenceId) params.set("conferenceId", meta.conferenceId);
    if (meta?.threadId) params.set("threadId", meta.threadId);
    if (meta?.participants) params.set("participants", JSON.stringify(meta.participants));
    try {
      const resp = await fetch(`${base}/recordings/session?${params.toString()}`, {
        method: "POST",
        credentials: "include",
        headers: authHeaders(),
      });
      if (resp.ok) {
        mode = "streaming";
        startHeartbeat();
        // Flush whatever buffered during probing, then drop the legacy copies.
        for (const s of allStreams()) {
          if (s.legacyChunks.length) {
            s.legacyChunks.forEach((blob, i) => s.queue.push({ seq: i, blob }));
            s.legacyChunks = [];
            void pump(s);
          }
        }
        // eslint-disable-next-line no-console
        console.info(`[recording] segmented upload session opened (${recordingId})`);
      } else {
        mode = "legacy";
        // eslint-disable-next-line no-console
        console.warn(`[recording] /session returned ${resp.status}; falling back to single-POST upload`);
      }
    } catch (e) {
      mode = "legacy";
      // eslint-disable-next-line no-console
      console.warn("[recording] /session unreachable; falling back to single-POST upload:", e);
    }
  })();

  function startHeartbeat(): void {
    if (heartbeatTimer) return;
    heartbeatTimer = setInterval(() => {
      void fetch(`${base}/recordings/${recordingId}/heartbeat`, {
        method: "POST",
        credentials: "include",
        headers: authHeaders(),
      }).catch(() => { /* best-effort */ });
    }, HEARTBEAT_INTERVAL_MS);
  }

  function stopHeartbeat(): void {
    if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
  }

  // Send one segment, retrying transient failures. Returns true once the chunk
  // is durably on the server (2xx ack or confirmed-duplicate), false only on a
  // terminal condition: the session is gone (410), a seq gap (409), the
  // uploader was aborted/detached, or — once finalizing — the drain deadline
  // passed. While the recording is live this retries indefinitely with capped
  // backoff so a network drop keeps the chunk queued instead of dropping it.
  async function sendSegment(s: StreamState, seq: number, blob: Blob): Promise<boolean> {
    const qp = new URLSearchParams({ kind: s.kind, seq: String(seq) });
    qp.set("mime", s.mime);
    if (s.kind === "audio") {
      qp.set("userId", s.userId || "");
      // offsetMs is the per-track join offset, persisted server-side only on
      // the first segment that creates the audio entry (see graphql-server-dev
      // recordingUpload.js: `if (!a) { … offsetMs }`). Sending it on every
      // chunk made nginx logs read as a "frozen" value across segments. Gate
      // it to seq=0 — older clients still send it on every chunk and the
      // server tolerates that, so this is purely a logging-clarity change.
      if (seq === 0) qp.set("offsetMs", String(s.offsetMs));
    }
    const url = `${base}/recordings/${recordingId}/segment?${qp.toString()}`;
    let attempt = 0;
    for (;;) {
      if (aborted || detached) return false;
      if (finalizing && Date.now() > drainDeadline) return false;
      try {
        const resp = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: authHeaders({ "Content-Type": "application/octet-stream" }),
          body: blob,
        });
        if (resp.ok) return true; // 2xx covers both fresh ack and duplicate-ack
        if (resp.status === 410 || resp.status === 409) return false; // session gone / seq gap — terminal
        if (
          resp.status === 413 || // payload too large — same blob retried = same 413
          resp.status === 400 || // malformed (shouldn't happen, but don't loop)
          resp.status === 415 || // unsupported media type
          resp.status === 422    // unprocessable entity
        ) {
          // Terminal for THIS stream: the same blob will fail identically.
          // Without this branch a single 413 (e.g. a video chunk that
          // briefly burst over SEGMENT_MAX_BYTES on a keyframe) would
          // stall the pump forever — pump waits on this chunk, the
          // queue behind it grows, MediaRecorder keeps producing into
          // a dead pipeline, and no further uploads or finalize ever
          // happen. Marking the stream failed lets the OTHER kind
          // (audio while this is video, or vice-versa) continue and
          // /finalize seal the partial recording at stop time.
          // eslint-disable-next-line no-console
          console.error(
            `[recording] segment send terminal failure (${s.kind}${s.userId ? ":" + s.userId : ""}) seq=${seq} size=${blob.size}B status=${resp.status} — this stream is now dead, other tracks continue`,
          );
          return false;
        }
        if (resp.status === 401) { await refreshToken(); }
        // 401 (after refresh) / 5xx / transient → backoff and retry
      } catch { /* network error → backoff and retry */ }
      const backoff = Math.min(SEGMENT_RETRY_CAP_MS, SEGMENT_RETRY_BASE_MS * Math.pow(2, Math.min(attempt, 6)));
      attempt++;
      await new Promise((r) => setTimeout(r, backoff));
    }
  }

  function pump(s: StreamState): Promise<void> {
    if (s.pumping) return s.pumping;
    s.pumping = (async () => {
      while (s.queue.length && !s.failed && !aborted && !detached) {
        // Stall the pump while the conference is on a degraded link —
        // see the top-of-file comment for the rationale. The MediaRecorder
        // keeps producing chunks; they accumulate in `s.queue` until the
        // quality hook flips us back on, at which point we drain the
        // backlog in order.
        if (uploadPaused) {
          // Once finalizing, don't wait past the drain deadline — otherwise a
          // recording that stops on a degraded link stalls here and finalizes
          // empty (bug: two recordings → first saves empty).
          await waitWhilePaused(() => finalizing && Date.now() > drainDeadline);
          if (s.failed || aborted || detached) break;
        }
        const head = s.queue[0];
        const ok = await sendSegment(s, head.seq, head.blob);
        if (ok) {
          s.queue.shift(); // free the chunk
        } else if (aborted || detached) {
          break; // quiet stop — not a stream failure
        } else {
          // Terminal (410/409) or the finalize drain deadline passed.
          s.failed = true;
          mode = "broken";
          // eslint-disable-next-line no-console
          console.error(`[recording] segment stream failed (${s.kind}${s.userId ? ":" + s.userId : ""}) — recording may be incomplete`);
          break;
        }
      }
      s.pumping = null;
    })();
    return s.pumping;
  }

  function push(s: StreamState, blob: Blob): void {
    if (aborted || detached) return;
    const seq = s.produced++;
    if (mode === "streaming") {
      s.queue.push({ seq, blob });
      void pump(s);
    } else if (mode === "broken") {
      // Session lost mid-stream (the server already auto-finalized what it had)
      // — discard instead of growing memory; nothing more can be recovered.
    } else {
      // probing (flushed on session open) or legacy (buffered for single POST)
      s.legacyChunks.push(blob);
    }
  }

  async function drainStreaming(): Promise<void> {
    // Bound the wait: un-acked segments retry until this deadline, then give up
    // so a still-down network can't hang finalize forever.
    finalizing = true;
    drainDeadline = Date.now() + FINALIZE_DRAIN_MS;
    // Pushes have stopped by finalize time; loop until every queue is drained
    // (or a stream gave up).
    for (;;) {
      const active = allStreams().filter((s) => !s.failed && (s.queue.length > 0 || s.pumping));
      if (active.length === 0) break;
      active.forEach((s) => void pump(s));
      await Promise.all(allStreams().map((s) => s.pumping).filter(Boolean));
    }
  }

  function buildOffsets(): Record<string, number> {
    const offsets: Record<string, number> = {};
    for (const [uid, s] of audio.entries()) {
      if (s.offsetMs > 0) offsets[uid] = Math.round(s.offsetMs);
    }
    return offsets;
  }

  async function finalizeStreaming(
    durationSec: number,
    onProgress?: (p: UploadProgress) => void,
  ): Promise<RecordingResult | null> {
    await drainStreaming();
    stopHeartbeat();
    if (onProgress) onProgress({ loaded: 0, total: 0, phase: "processing" });
    const params = new URLSearchParams({ duration: String(durationSec) });
    const offsets = buildOffsets();
    if (Object.keys(offsets).length > 0) params.set("offsets", JSON.stringify(offsets));
    const resp = await fetch(`${base}/recordings/${recordingId}/finalize?${params.toString()}`, {
      method: "POST",
      credentials: "include",
      headers: authHeaders(),
    });
    if (!resp.ok) throw new Error(`Finalize failed: ${resp.status} ${await resp.text().catch(() => "")}`.trim());
    const data = await resp.json();
    return { url: `${base}${data.url}`, filename: data.filename, size: data.size, id: data.id };
  }

  function legacyUpload(
    durationSec: number,
    onProgress?: (p: UploadProgress) => void,
  ): Promise<RecordingResult> {
    const formData = new FormData();
    const videoBlob = new Blob(video.legacyChunks, { type: videoMime });
    formData.append("recording", videoBlob, "conference-recording.webm");

    const offsets: Record<string, number> = {};
    for (const [uid, s] of audio.entries()) {
      const safeId = sanitizeUserId(uid);
      const blob = new Blob(s.legacyChunks, { type: s.mime });
      if (!safeId || blob.size === 0) continue;
      const ext = s.mime.includes("mp4") ? "m4a" : "webm";
      formData.append(`audio_${safeId}`, blob, `track-${safeId}.${ext}`);
      if (s.offsetMs > 0) offsets[safeId] = Math.round(s.offsetMs);
    }

    const params = new URLSearchParams();
    if (meta?.conferenceId) params.set("conferenceId", meta.conferenceId);
    if (meta?.threadId) params.set("threadId", meta.threadId);
    if (meta?.participants) params.set("participants", JSON.stringify(meta.participants));
    if (durationSec > 0) params.set("duration", String(durationSec));
    if (Object.keys(offsets).length > 0) params.set("offsets", JSON.stringify(offsets));

    const qs = params.toString();
    const uploadUrl = `${base}/recordings/upload${qs ? `?${qs}` : ""}`;

    return new Promise<RecordingResult>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let idleTimer: ReturnType<typeof setTimeout> | null = null;
      let hardTimer: ReturnType<typeof setTimeout> | null = null;
      let settled = false;

      const clearTimers = () => {
        if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
        if (hardTimer) { clearTimeout(hardTimer); hardTimer = null; }
      };
      const failWith = (err: Error) => {
        if (settled) return;
        settled = true; clearTimers();
        try { xhr.abort(); } catch { /* noop */ }
        reject(err);
      };
      const resetIdle = () => {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(() => {
          failWith(Object.assign(new Error(`Upload stalled: no progress for ${UPLOAD_IDLE_TIMEOUT_MS / 1000}s`), { code: "UPLOAD_STALLED" }));
        }, UPLOAD_IDLE_TIMEOUT_MS);
      };
      hardTimer = setTimeout(() => {
        failWith(Object.assign(new Error(`Upload exceeded max duration ${UPLOAD_MAX_DURATION_MS / 1000}s`), { code: "UPLOAD_MAX_DURATION" }));
      }, UPLOAD_MAX_DURATION_MS);

      xhr.open("POST", uploadUrl, true);
      xhr.withCredentials = true;
      if (token) xhr.setRequestHeader("Authorization", token);

      xhr.upload.onprogress = (e: ProgressEvent) => {
        resetIdle();
        if (onProgress) onProgress({ loaded: e.loaded, total: e.lengthComputable ? e.total : 0, phase: "uploading" });
      };
      xhr.upload.onloadstart = resetIdle;
      xhr.upload.onload = () => {
        resetIdle();
        if (onProgress) onProgress({ loaded: 0, total: 0, phase: "processing" });
      };
      xhr.onprogress = resetIdle;
      xhr.onerror = () => failWith(new Error("Upload network error"));
      xhr.ontimeout = () => failWith(new Error("Upload timed out"));
      xhr.onabort = () => { if (!settled) failWith(new Error("Upload aborted")); };
      xhr.onload = () => {
        if (settled) return;
        clearTimers();
        const status = xhr.status;
        if (status < 200 || status >= 300) {
          settled = true;
          reject(Object.assign(new Error(`Upload failed: ${status} ${xhr.responseText || ""}`.trim()), { status }));
          return;
        }
        let data: { url: string; filename: string; size: number; id?: string };
        try { data = JSON.parse(xhr.responseText); }
        catch { settled = true; reject(new Error("Upload succeeded but response was not JSON")); return; }
        settled = true;
        resolve({ url: `${base}${data.url}`, filename: data.filename, size: data.size, id: data.id });
      };

      resetIdle();
      xhr.send(formData);
    });
  }

  return {
    recordingId,
    pushVideo(blob: Blob, mime: string) {
      videoMime = mime || videoMime;
      video.mime = videoMime;
      push(video, blob);
    },
    pushAudio(userId: string, mime: string, offsetMs: number, blob: Blob) {
      const uid = sanitizeUserId(userId);
      if (!uid) return;
      let s = audio.get(uid);
      if (!s) {
        s = { kind: "audio", userId: uid, mime, offsetMs, legacyChunks: [], queue: [], produced: 0, pumping: null, failed: false };
        audio.set(uid, s);
      }
      push(s, blob);
    },
    async finalize(durationSec: number, onProgress?: (p: UploadProgress) => void): Promise<RecordingResult | null> {
      await ready; // ensure the mode is decided
      if (aborted) return null;
      stopHeartbeat();
      if (mode === "streaming" || mode === "broken") {
        try {
          return await finalizeStreaming(durationSec, onProgress);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error("[recording] streaming finalize failed:", e);
          // Cannot fall back to single-POST (chunks were discarded as acked).
          return null;
        }
      }
      // legacy
      return legacyUpload(durationSec, onProgress);
    },
    abort() {
      aborted = true;
      stopHeartbeat();
      // Best-effort: tell the server to discard the staging session.
      if (mode === "streaming" || mode === "broken") {
        void fetch(`${base}/recordings/${recordingId}/abort`, {
          method: "POST",
          credentials: "include",
          headers: authHeaders(),
        }).catch(() => { /* best-effort */ });
      }
      // Drop buffers.
      for (const s of allStreams()) { s.legacyChunks = []; s.queue = []; }
      audio.clear();
    },
    detach() {
      // Component unmounted mid-recording: stop streaming locally but do NOT
      // tell the server to discard — its sweep auto-finalizes the staged bytes,
      // so the recording up to this point is preserved.
      detached = true;
      stopHeartbeat();
    },
  };
}
